// src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

/**
 * Provee:
 *  - user: objeto con datos de usuario o null
 *  - token: JWT o null
 *  - loading: booleano durante login
 *  - login(credentials): fn para autenticar
 *  - logout(): fn para cerrar sesión
 */
export const AuthProvider = ({ children }) => {
  // Inicialización lazy — lee del storage en el primer render (sin efecto extra).
  // Siempre localStorage (compartido entre pestañas/ventanas nuevas, a diferencia
  // de sessionStorage que es por-pestaña); se revisa sessionStorage como fallback
  // por compatibilidad con sesiones que hayan quedado ahi de antes de este cambio.
  // El limite real de la sesion lo pone el JWT (8h, validado por el backend).
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (!stored || stored === 'undefined') return null;
      return JSON.parse(stored);
    } catch {
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      return null;
    }
  });

  const [token, setToken] = useState(() => {
    try {
      const t = localStorage.getItem('token') || sessionStorage.getItem('token');
      return (t === 'undefined' || !t) ? null : t;
    } catch {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      return null;
    }
  });

  const [loading, setLoading] = useState(false);

  // Ref para evitar doble validación concurrente al montar
  const sessionChecked = useRef(false);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    // C5: desconectar socket al cerrar sesión
    if (window.notificationsSocket) {
      window.notificationsSocket.disconnect();
      window.notificationsSocket = null;
    }
  }, []);

  // Guarda sesion (storage) y redirige -- comun al login normal y al segundo
  // paso de MFA (verifyMfa), que llegan al mismo punto por caminos distintos.
  const completarSesion = useCallback(async (u, t) => {
    // Guardar token en storage ANTES de llamar a getProfile (el interceptor lo lee).
    // Siempre localStorage: persiste entre pestañas/ventanas nuevas y reinicios del
    // navegador. El JWT expira solo a las 8h (JWT_EXPIRES_IN), que es el limite real.
    localStorage.setItem('token', t);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');

    // Obtener perfil completo (con esJefatura)
    const { data: completeUser } = await authAPI.getProfile();

    // Actualizar state
    setUser(completeUser);
    setToken(t);

    // Persistir usuario completo
    localStorage.setItem('user', JSON.stringify(completeUser));

    // Redirigir según rol (full page reload intencional para limpiar estado)
    const rawBasePath = window.APP_CONFIG?.BASE_PATH ?? '/';
    const basePath = rawBasePath === '/' ? '' : rawBasePath;
    const pendingRedirect = sessionStorage.getItem('hd_post_login_redirect');
    sessionStorage.removeItem('hd_post_login_redirect');
    const dest = pendingRedirect
      ? `${basePath}${pendingRedirect}`
      : (u.role === 'admin' || u.role === 'soporte')
        ? `${basePath}/dashboard`
        : u.role === 'sap'
          ? `${basePath}/mis-activos`
          : `${basePath}/mis-tickets`;
    window.location.href = dest;
  }, []);

  const login = useCallback(async (credentials) => {
    setLoading(true);
    try {
      const { username, password } = credentials;
      const response = await authAPI.login({ username, password });

      // Cuenta central con MFA (TOTP) activado: password correcta, pero
      // falta el segundo factor. No hay sesion todavia -- se devuelve el
      // desafio para que Login.jsx muestre la pantalla de codigo.
      if (response.data.mfaRequired) {
        return { mfaRequired: true, mfaToken: response.data.mfaToken };
      }

      // Cuenta local (no SSO) que todavia no tiene MFA activado: password
      // correcta, pero login() no entrega sesion -- exige activarlo primero.
      // Login.jsx muestra el QR obligatorio con el mfaSetupToken temporal.
      if (response.data.mfaSetupRequired) {
        return { mfaSetupRequired: true, mfaSetupToken: response.data.mfaSetupToken };
      }

      const { user: u, token: t } = response.data;
      await completarSesion(u, t);
      return { mfaRequired: false };
    } catch (error) {
      logout();
      throw new Error(error.response?.data?.message || error.message || 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  }, [logout, completarSesion]);

  // Segundo paso del login local cuando login() devolvio mfaRequired: canjea
  // el mfaToken + el codigo de 6 digitos de la app authenticator.
  const verifyMfa = useCallback(async (mfaToken, codigo) => {
    setLoading(true);
    try {
      const response = await authAPI.verifyMfaLogin({ mfaToken, codigo });
      const { user: u, token: t } = response.data;
      await completarSesion(u, t);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Código incorrecto');
    } finally {
      setLoading(false);
    }
  }, [completarSesion]);

  // Paso final de la activación obligatoria de MFA (ver login() de arriba):
  // confirma el primer código TOTP con el mfaSetupToken temporal y, si es
  // válido, el backend entrega la sesión real de una vez (no hay que
  // volver a loguearse).
  const completarSetupMfaObligatorio = useCallback(async (mfaSetupToken, codigo) => {
    setLoading(true);
    try {
      const response = await authAPI.mfaConfirmSetupForzado(mfaSetupToken, codigo);
      const { user: u, token: t } = response.data;
      await completarSesion(u, t);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Código incorrecto');
    } finally {
      setLoading(false);
    }
  }, [completarSesion]);

  // Login SSO: la constancia ya viene validada por api_vicsa (Microsoft +
  // dominio permitido + acceso otorgado) -- solo queda canjearla por la
  // sesión propia de HelpDesk, mismo flujo de persistencia que el login normal.
  const loginWithAssertion = useCallback(async (assertion) => {
    setLoading(true);
    try {
      const response = await authAPI.sso(assertion);
      const { user: u, token: t } = response.data;

      localStorage.setItem('token', t);
      sessionStorage.removeItem('token');

      const { data: completeUser } = await authAPI.getProfile();

      setUser(completeUser);
      setToken(t);

      localStorage.setItem('user', JSON.stringify(completeUser));
      sessionStorage.removeItem('user');

      const rawBasePath = window.APP_CONFIG?.BASE_PATH ?? '/';
      const basePath = rawBasePath === '/' ? '' : rawBasePath;
      const pendingRedirect = sessionStorage.getItem('hd_post_login_redirect');
      sessionStorage.removeItem('hd_post_login_redirect');
      const dest = pendingRedirect
        ? `${basePath}${pendingRedirect}`
        : (u.role === 'admin' || u.role === 'soporte')
          ? `${basePath}/dashboard`
          : u.role === 'sap'
            ? `${basePath}/mis-activos`
            : `${basePath}/mis-tickets`;
      window.location.href = dest;
    } catch (error) {
      logout();
      throw new Error(error.response?.data?.message || error.message || 'Error de autenticación SSO');
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Efecto único: valida token con el servidor una sola vez al montar.
  // El useState lazy ya hidrata user/token del storage, así que no hay flash de contenido.
  useEffect(() => {
    if (sessionChecked.current) return;
    sessionChecked.current = true;

    if (!token) return; // Sin token → no hay sesión que validar

    let cancelled = false;

    const validate = async () => {
      try {
        const { data: freshUser } = await authAPI.getProfile();
        if (cancelled) return;
        setUser(freshUser);
        localStorage.setItem('user', JSON.stringify(freshUser));
      } catch {
        if (!cancelled) logout();
      }
    };

    validate();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirigir al dashboard si el usuario llega a /login ya autenticado
  useEffect(() => {
    if (!user?.id || !user?.role) return;
    const rawBasePath = window.APP_CONFIG?.BASE_PATH ?? '/portal_ti';
    const basePath = rawBasePath === '/' ? '' : rawBasePath;
    const isLoginOrRoot = [
      `${basePath}/login`, `${basePath}/`, basePath || '/'
    ].includes(window.location.pathname);
    if (!isLoginOrRoot) return;

    const pendingRedirect = sessionStorage.getItem('hd_post_login_redirect');
    sessionStorage.removeItem('hd_post_login_redirect');
    const dest = pendingRedirect
      ? `${basePath}${pendingRedirect}`
      : (user.role === 'admin' || user.role === 'soporte')
        ? `${basePath}/dashboard`
        : user.role === 'sap'
          ? `${basePath}/mis-activos`
          : `${basePath}/mis-tickets`;
    window.location.href = dest;
  }, [user?.id, user?.role]); // Solo id y role — estables tras el primer fetch

  const value = React.useMemo(() => ({
    user,
    token,
    loading,
    login,
    verifyMfa,
    completarSetupMfaObligatorio,
    loginWithAssertion,
    logout,
  }), [user, token, loading, login, verifyMfa, completarSetupMfaObligatorio, loginWithAssertion, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
